import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import './Modal.scss';
import SvgIconClose from '../../../assets/RemoteOrder/icons/CloseWithoutCircle.svg';
import SodiumWarning from '../../../assets/RemoteOrder/icons/SodiumWarning.svg';
import BackButtonIcon from '../../../assets/RemoteOrder/icons/BackArrow.svg';
import Button from '../../Atoms/Button/Button';
import { addFocusListener, onTabEsc } from '../../../Utils/componentUtils';
import ModalProps from '../../Interfaces/ModalInterface';
import Heading from '../../Atoms/Heading/Heading';
import { createPortal } from 'react-dom';
import { componentRenderCheck, multipleAndCheck, multipleOrCheck, ternaryCheck } from '../../../Utils/WindowUtils';
import useStore from '../../../store/useStore';
import { isEmpty } from 'lodash';
import { CLOSE_ICON, ESC } from '../../../constants/AnalyticsConstants';

const Modal: React.FC<ModalProps> = ({
  isShown = true,
  onButtonClick,
  title,
  isClosebtn = false,
  isBackBtn = false,
  showIcon = false,
  description,
  testId,
  className = 'modalData',
  modalTestIdIdentifier = 'common',
  modalParentClass = '',
  componentParentClass = '',
  isModalBottomSlide = false,
  showCurvedTopModal = false,
  isCloseModalOnEscape = true,
  handleBackFunction,
  ...props
}) => {
  const { focusReturnToTriggerElement } = useStore();
  const [activeElement, setActiveElement] = useState<any>('');
  const [modalSlideToggleUp, setModalSlideToggleUp] = useState<boolean>(isModalBottomSlide);
  const modalRef = useRef<Element | null>(null);
  const firstFocusableElementRef = useRef<any>(null);
  const lastFocusableElementRef = useRef<any>(null);
  const focusTrapId = document.getElementById('react-popup') as HTMLElement;

    useEffect(() => {
    let removeEscListener: (() => void) | undefined;
    if (isShown) {
      if(isCloseModalOnEscape){
        removeEscListener = onTabEsc(() => {
          onButtonClick(ESC);
        });
      }
      const ModalDOMRef = document.querySelector(".modalPopup__container");
      if (ModalDOMRef) {
        modalRef.current = ModalDOMRef;
        addFocusListener(modalRef, firstFocusableElementRef, lastFocusableElementRef);
        if (!isEmpty(focusReturnToTriggerElement)) {
            const focusReturnToTriggerElementRef = document.querySelector(`.${focusReturnToTriggerElement}`) as HTMLElement;
            focusReturnToTriggerElementRef?.focus();
        }
      }
    }
    const reactPopup = document.getElementById('react-popup') as HTMLElement;
    const modalPopup = reactPopup?.querySelector('.modalPopup');
    const focusModalPopup = reactPopup?.querySelector('.focus-modal-popup');
    const stickTo = document.getElementById('stick-to') as HTMLElement;
    const headerSticky = stickTo?.querySelector('.header-sticky');
    const homePageNew = stickTo?.querySelector('#homePageNew');
    const componentFooter = stickTo?.querySelector('.component_footer');
    const htmlElement = document.querySelector('.cms') as HTMLElement;

    if (multipleAndCheck(modalPopup, focusModalPopup, headerSticky, homePageNew, componentFooter)) {
      focusModalPopup?.setAttribute('aria-hidden', ternaryCheck(isShown, 'false', 'true'));
      headerSticky?.setAttribute('aria-hidden', ternaryCheck(isShown, 'true', 'false'));
      homePageNew?.setAttribute('aria-hidden', ternaryCheck(isShown, 'true', 'false'));
      componentFooter?.setAttribute('aria-hidden', ternaryCheck(isShown, 'true', 'false'));
      htmlElement?.style?.setProperty('overflow-y', 'hidden');
    }
    return () => {
      if (multipleAndCheck(focusModalPopup, headerSticky, homePageNew, componentFooter)) {
        focusModalPopup?.removeAttribute('aria-hidden');
        headerSticky?.removeAttribute('aria-hidden');
        homePageNew?.removeAttribute('aria-hidden');
        componentFooter?.removeAttribute('aria-hidden');
      }
      htmlElement?.style?.setProperty('overflow-y', 'initial');
      removeEscListener?.();
    };
  }, [isShown, isBackBtn, isClosebtn]);

  useLayoutEffect(() => {
    setActiveElement(document.activeElement);
  }, [])

  const modalSlideClass = ternaryCheck(isModalBottomSlide, "modalBottomSlide", "");
  const slideClass = ternaryCheck(modalSlideToggleUp, 'slideUp', 'slideDown');
  const curvedTopClass = ternaryCheck(showCurvedTopModal, 'showCurvedTopModal', '');
  const combinedClassName = `modalPopup__container ${slideClass} ${curvedTopClass} ${className}`;

  return ternaryCheck(isShown,
    createPortal(
      <div data-testid={testId} id="modal" className={`focus-modal-popup ${componentParentClass}`} >
        <div className={`modalPopup ${modalParentClass} ${modalSlideClass} `}>
          <div className="modalPopup__wrapper">
            <dialog
              className={combinedClassName}
              aria-labelledby="title__labelledby"
              aria-describedby="description__describedby"
              aria-modal="true"
              role="alertdialog"
              data-modal-background-click="disabled" //for testing
              open
            >
              <div className="modal_header">
                {componentRenderCheck(isBackBtn,
                  <Button
                    testAutoId={'modal-popup-backBtn'}
                    className="modalPopup__backBtn"
                    onClick={() => {
                      if (activeElement) {
                        activeElement?.focus?.();
                      }
                      handleBackFunction?.()
                    }}
                    icon={<BackButtonIcon />}
                    aria-label="back"
                  ></Button>)
                }
                {componentRenderCheck(isClosebtn,
                  <Button
                    testAutoId={'modal-popup-closeBtn'}
                    className="modalPopup__close"
                    onClick={() => {
                      if (activeElement) {
                        activeElement?.focus?.();
                      }
                      if (modalSlideToggleUp) {
                        setModalSlideToggleUp(!modalSlideToggleUp)
                        setTimeout(() => {
                          onButtonClick(CLOSE_ICON)
                        }, 500);
                      }
                      else {
                        onButtonClick(CLOSE_ICON)
                      }
                    }}
                    icon={<SvgIconClose />}
                    aria-label={(window as any).closeText}
                  ></Button>)
                }
                {componentRenderCheck((multipleOrCheck(title, showIcon)), (
                  <div className="modalPopup__header">
                    {componentRenderCheck(showIcon, <SodiumWarning />)}
                    <Heading id="title__labelledby" className="modalPopup_title" testAutoId={`${modalTestIdIdentifier}-modal-title`} level="h2" title={title} />
                  </div>
                ))}
              </div>
              {componentRenderCheck(!!description, <div className="modalPopup__body" data-testauto-id={`${modalTestIdIdentifier}-modal-description`}>{description}</div>)}
              <div className="modalPopup__button" id="description__describedby">{props.children}</div>
            </dialog>
          </div>
        </div>
      </div>, focusTrapId,
    ),
    null)
};

export default Modal;